import {
  applyCoordinatesToAssetBody,
  buildAddressGeocodeQuery,
  isDefaultJerusalemCoordinates,
  parseAssetCoordinates,
  type AddressGeocodeLabels,
} from './assetGeo';
import { applyPostalCodeToAssetBody } from './assetAddressPostalCode';
import { geocodeAssetAddressDetails } from './resolveIsraelAddressGeo';

export async function resolveAssetBodyGeo(
  body: Record<string, unknown>,
  labels?: AddressGeocodeLabels,
): Promise<Record<string, unknown>> {
  const existing = parseAssetCoordinates(body);
  const hasManualCoords = existing !== null && !isDefaultJerusalemCoordinates(existing);
  const query = buildAddressGeocodeQuery(body, labels);
  if (query) {
    const fromAddress = await geocodeAssetAddressDetails(body, labels);
    if (fromAddress) {
      let next = applyCoordinatesToAssetBody(body, fromAddress);
      if (fromAddress.postalCode) {
        next = applyPostalCodeToAssetBody(next, fromAddress.postalCode);
      }
      return next;
    }
    if (hasManualCoords) {
      return applyCoordinatesToAssetBody(body, existing);
    }
    throw new Error('GEOCODE_REQUIRED');
  }
  if (hasManualCoords) {
    return applyCoordinatesToAssetBody(body, existing);
  }
  throw new Error('GEOCODE_REQUIRED');
}

/** Update PATCH: re-geocode when address changes; otherwise keep stored coordinates. */
export async function resolveAssetBodyGeoForUpdate(
  body: Record<string, unknown>,
  baseline: Record<string, unknown>,
  labels?: AddressGeocodeLabels,
): Promise<Record<string, unknown>> {
  const bodyQuery = buildAddressGeocodeQuery(body, labels);
  const baselineQuery = buildAddressGeocodeQuery(baseline, labels);
  const addressChanged = (bodyQuery ?? '') !== (baselineQuery ?? '');

  if (addressChanged && bodyQuery) {
    return await resolveAssetBodyGeo(body, labels);
  }

  const fromBaseline = parseAssetCoordinates(baseline);
  if (fromBaseline) {
    return applyCoordinatesToAssetBody(body, fromBaseline);
  }
  const fromBody = parseAssetCoordinates(body);
  if (fromBody) {
    return applyCoordinatesToAssetBody(body, fromBody);
  }
  try {
    return await resolveAssetBodyGeo(body, labels);
  } catch {
    return body;
  }
}
